import { AUTOMATION_DATE_SOURCE } from '@constants/automations';
import { getDaysInMonth } from 'date-fns';
import { MINUTES_IN_DAY, toTime } from '@helpers/dates';
import { LEAP_YEAR, formatBirthDate } from '@helpers/contact-demographics';

export interface DateTrigger {
    source: number;
    month?: number | null;
    day?: number | null;
    year?: number | null;
    offsetDays: number;
    sendMinute: number;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const DATE_TIMING = {
    ON: 'on',
    BEFORE: 'before',
    AFTER: 'after',
} as const;

export type DateTiming = (typeof DATE_TIMING)[keyof typeof DATE_TIMING];

export const MAX_OFFSET_DAYS = 365;

const DEFAULT_SEND_MINUTE = 9 * 60;

const browserUtcOffset = () => new Date().getTimezoneOffset();

export const toUtcMinute = (localMinute: number, utcOffset = browserUtcOffset()) => localMinute + utcOffset;

export const toLocalMinute = (utcMinute: number, utcOffset = browserUtcOffset()) =>
    (((utcMinute - utcOffset) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;

export const defaultDateTrigger = (): DateTrigger => ({
    source: AUTOMATION_DATE_SOURCE.BIRTHDAY,
    offsetDays: 0,
    sendMinute: toUtcMinute(DEFAULT_SEND_MINUTE),
});

export const timingOf = (offsetDays: number): DateTiming =>
    offsetDays < 0 ? DATE_TIMING.BEFORE : offsetDays > 0 ? DATE_TIMING.AFTER : DATE_TIMING.ON;

export const offsetFor = (timing: DateTiming, days: number) =>
    timing === DATE_TIMING.BEFORE ? -days : timing === DATE_TIMING.AFTER ? days : 0;

export const daysInMonth = (month: number, year?: number | null) =>
    getDaysInMonth(new Date(year ?? LEAP_YEAR, month - 1));

const LEAP_CYCLE_YEARS = 8;

export const sendsAfter = (date: DateTrigger, year: number, now = new Date()) => {
    const month = date.month ?? 1;
    const day = date.day ?? 1;
    return (
        day <= daysInMonth(month, year) &&
        Date.UTC(year, month - 1, day + date.offsetDays, 0, date.sendMinute) > now.getTime()
    );
};

export const firstYearAhead = (date: DateTrigger, today = new Date()) =>
    Array.from({ length: LEAP_CYCLE_YEARS }, (_, index) => today.getFullYear() + index).find(year =>
        sendsAfter(date, year, today),
    ) ?? today.getFullYear() + 1;

export const isCompleteDateTrigger = (date: DateTrigger | null | undefined) =>
    !!date && (date.source === AUTOMATION_DATE_SOURCE.BIRTHDAY || (!!date.month && !!date.day));

const formatFixedDate = ({ day, month, year }: DateTrigger, locale: string) =>
    formatBirthDate({ birthYear: year ?? null, birthMonth: month ?? null, birthDay: day ?? null }, locale);

export const describeDateTrigger = (
    date: DateTrigger,
    t: Translate,
    locale: string,
    utcOffset = browserUtcOffset(),
) => {
    const days = Math.abs(date.offsetDays);
    const timing = timingOf(date.offsetDays);
    return [
        date.source === AUTOMATION_DATE_SOURCE.BIRTHDAY
            ? t('AutomationsPage.trigger.birthday')
            : formatFixedDate(date, locale),
        timing === DATE_TIMING.ON
            ? t('AutomationsPage.trigger.onTheDay')
            : t(`AutomationsPage.trigger.${timing === DATE_TIMING.BEFORE ? 'daysBefore' : 'daysAfter'}`, {
                  count: days,
              }),
        toTime(toLocalMinute(date.sendMinute, utcOffset)),
    ]
        .filter(Boolean)
        .join(' · ');
};
